import { useContext, useEffect, useState } from 'react';
import { Buttons, FlexContainer, ModalContext, Modals, Text, Inputs } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { useTranslation } from 'Hooks/useTranslation';
import useValidateClabe, { getErrorContext, hasError } from 'Hooks/useValidateClabe';
import { removeOverflow } from 'Styles/utils/utils';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import { usePatchUserClabe } from './hooks';
import { ClabeModalProps } from './types';

const initialState = {
  clabe: { value: '', isValid: false },
  fullName: { value: '', isValid: false },
};

const REFECTH_KEY = 'refetch';

const ClabeModal = ({ data }: ClabeModalProps): JSX.Element => {
  const { hideModal } = useContext(ModalContext);
  const { t } = useTranslation(locizeNS.MGM);
  const [formValues, setFormValues] = useState(initialState);
  const closeModal = (): void => {
    removeOverflow();
    data?.refetch();
    hideModal();
  };
  const { mutateAsync } = usePatchUserClabe(closeModal);
  const {
    isValid: isValidClabe,
    error,
    isLoading,
  } = useValidateClabe(formValues.clabe.value?.toString() || '');

  const handleSubmit = async (): Promise<void> => {
    if (formValues.clabe.value && formValues.fullName.value) {
      await mutateAsync({
        clabe: formValues?.clabe?.value?.toString(),
        fullName: formValues?.fullName?.value?.toString(),
      });
    }
  };

  useEffect(() => {
    if (data) {
      const newFormValues = { ...initialState };
      Object.entries(data).forEach(([key, value]) => {
        if (key !== REFECTH_KEY) {
          newFormValues[key] = {
            value: value?.toString() || '',
            isValid: Boolean(value),
          };
        }
      });
      setFormValues(newFormValues);
    }
  }, [data]);

  return (
    <Modals.Centered maxSize="S" onClose={closeModal}>
      <FlexContainer direction="column" fullWidth gap="dimension_16">
        <img src={`${TAXDOWN_ASSETS_DOMAIN}/mgm/CLABE.svg`} alt="clabe-modal-icon" />
        <Text color="dark" type="headline" size="XS_L_S" align="center">
          {t('Referral.ClabeModal.Title')}
        </Text>
        <Text color="fresh" type="paragraphs" size="XS_L_S" align="center">
          {t('Referral.ClabeModal.Subtitle')}
        </Text>
        <Inputs.Text
          label={t('Referral.clabeModal.FullName.Label')}
          placeholder={t('Referral.clabeModal.FullName.placeholder')}
          value={formValues.fullName.value?.toString()}
          onChange={value => setFormValues({ ...formValues, fullName: { value, isValid: true } })}
        />
        <Inputs.Text
          label={t('Referral.clabeModal.Clabe.Label')}
          placeholder={t('Referral.clabeModal.Clabe.placeholder')}
          value={formValues.clabe.value?.toString()}
          hasError={!isLoading && hasError(formValues.clabe.value?.toString(), isValidClabe)}
          onChange={value =>
            setFormValues({
              ...formValues,
              clabe: {
                value,
                isValid: !isLoading && !hasError(formValues.clabe.value?.toString(), isValidClabe),
              },
            })
          }
          errorMsg={t(
            `filing:Filing.clabe.${getErrorContext(formValues.clabe.value?.toString(), error)}`
          )}
        />
        <FlexContainer justify="center">
          <Buttons.Classic
            disabled={!isValidClabe || !formValues.fullName.isValid || isLoading}
            label={t('Referral.clabeModal.CTA')}
            mode="primary"
            size="m"
            onClick={handleSubmit}
          />
        </FlexContainer>
      </FlexContainer>
    </Modals.Centered>
  );
};

export default ClabeModal;
